import { IAngularEvent, IPromise, IQService, ITimeoutService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { HttpErrorResponse } from "@angular/common/http";
import equal from "fast-deep-equal";
import matches from "lodash.matches";
import pick from "lodash.pick";
import { first, take, takeUntil } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { IStateInit } from "@gtmhub/core/routing";
import { storage } from "@gtmhub/core/storage";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import dayjs from "@gtmhub/dayjs";
import { CurrentEmployeeActions } from "@gtmhub/employees";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { DEFAULT_LIMIT, IMetricMethodologySettingsUI, OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { IGetGoalParams, IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { IInsightSelectorData } from "@gtmhub/insights";
import { AutoFieldsService, IMetricPrediction } from "@gtmhub/insights/services/auto-fields.service";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService, RequireAtLeastOne } from "@gtmhub/models";
import { OkrEvents } from "@gtmhub/okrs/events";
import {
  MetricDeadLine,
  MetricDeadLineEnum,
  MetricDeadLineType,
  disableDatesOutsideSessionTimeframe,
  metricFormStrings,
  restoreMetricDueDates,
  sendPredictionAnalyticsFeedback,
  sendTrackingAnalytics,
  setMetricDueDates,
} from "@gtmhub/okrs/metrics/utils";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { Ng1UiModalService } from "@gtmhub/shared/components/modal/ng1-modal.service";
import { IUnitFormatting } from "@gtmhub/shared/models";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { Ng1DestroyService } from "@gtmhub/shared/services";
import { INgRedux } from "@gtmhub/state-management/models";
import { ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { getCurrentUserId } from "@gtmhub/users";
import { PermissionsService } from "@gtmhub/users/permissions-service";
import { Flavor, recommendation } from "@webapp/analytics/models/analytics.model";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { CfMap, ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { Links } from "@webapp/links/models/links.models";
import { Milestone } from "@webapp/okrs/metrics/components/metric-milestone/metric-milestone.models";
import { ICheckin, Metric, MetricFormEventType, MetricMilestoneEventType, MetricTargetOperator } from "@webapp/okrs/metrics/models/metric.models";
import { MetricsFacade } from "@webapp/okrs/metrics/services/metrics-facade.service";
import { filterWorkflowSelectionProperties, generateInsightSelectorData, isFieldPartOfWorkflowUtil, parseDecimalInput } from "@webapp/okrs/utils/metric.utils";
import { CreateKrSuggestionDrawerLegacyComponent } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/create-kr-suggestion-drawer-legacy/create-kr-suggestion-drawer-legacy.component";
import { CreateKrSuggestionDrawerNoSuggestionReasonsMap } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/create-metric-suggestion-drawer/models/create-metric-suggestion-drawer.models";
import {
  SuggestionDrawerSectionType,
  SuggestionDrawerSuggestionGroupType,
} from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/models/suggestion-drawer.models";
import { SuggestionDrawerIndicatorStatePropagator } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/utils/state-propagator";
import {
  generateQuantivePlusKeyResultDescriptionSuggestionPayload,
  generateQuantivePlusSuggestionsPayload,
} from "@webapp/platform-intelligence/quantive-plus/utils/utils";
import { PIImproveSuggestionContext } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/models";
import { IQuantivePlusMetric } from "@webapp/platform-intelligence/shared/models";
import { PiFeatureVisibilityFacade } from "@webapp/platform-intelligence/shared/services/pi-feature-visibility-facade.service";
import { PiSuggestionSharedMetricStateService } from "@webapp/platform-intelligence/shared/services/pi-suggestion-shared-metric-state.service";
import { QuantivePlusSuggestionsFacade } from "@webapp/platform-intelligence/shared/services/quantive-plus-suggestions/quantive-plus-suggestions-facade.service";
import { mapMetricsToPiExistingKrs } from "@webapp/platform-intelligence/shared/utils/pi-adapter.utils";
import { getPiErrorData } from "@webapp/platform-intelligence/shared/utils/pi-errors.utils";
import { PiTrackingEventsEnum, PiTrackingFlowNameEnum } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { Session } from "@webapp/sessions/models/sessions.model";
import { PeopleSelectorRequest } from "@webapp/shared/components/people-selector/models/models";
import { generateMetricPredictionAnalytics } from "../auto-fields-util";
import { MetricService, metricProperties } from "../services/metric.service";

export interface IMetricFormParams {
  planningSessionId: string;
  metricId: string;
  id: string;
  flowName: PiTrackingFlowNameEnum;
}

type MetricPredictedFields = "target" | "targetOperator" | "format" | "initialValue";
const MetricPredictedFields: MetricPredictedFields[] = ["target", "targetOperator", "format", "initialValue"];

const bindStateToScope = (state: ISessionsStoreState & IAssigneesStoreState & ITeamsStoreState) => {
  return {
    assigneesIdMap: state.assignees.map,
    sessionIdMap: state.sessions.map,
    teams: state.teams.items,
    reduxItemsFetched: state.sessions.isFetched && state.assignees.isFetched && state.teams.isFetched,
    reduxItemsError: state.sessions.error || state.assignees.error || state.teams.error,
  };
};

export interface IMetricFormScope extends IModalScope, IExtendedRootScopeService, ReturnType<typeof bindStateToScope> {
  isMilestoneOn: boolean;
  milestoneErrorMessageId: string;
  lastMilestone: Milestone;
  flowId: string;
  flowName: PiTrackingFlowNameEnum;
  session: Session;
  metric: Metric;
  useCritical: boolean;
  metricsLimitReached: boolean;
  allowedActions: string[];
  dueDatePicker: {
    minDate: dayjs.Dayjs;
    maxDate: dayjs.Dayjs;
  };
  dates: {
    dueDate: string;
    softDueDate: string;
  };
  softDueDatePicker: {
    minDate: dayjs.Dayjs;
    maxDate: dayjs.Dayjs;
  };
  useDeadline: MetricDeadLine;
  goal: IGoal;
  isEdit: boolean;
  originalMetricType: string;
  errorMessage: string;
  errorMessageId: string;
  indicators: {
    loading?: IIndicator;
    saving?: IIndicator;
  };
  isFieldRequired: boolean;
  insightSelector: {
    data: IInsightSelectorData;
  };
  requiredCfNotEmpty: boolean;
  prediction: IMetricPrediction;
  shouldPredict: { [key: string]: boolean };
  metricMethodologySettings: IMetricMethodologySettingsUI & { metricsPerGoalLimit: number };
  shouldDisplayInsightSelector: boolean;
  peopleSelectorRequest: PeopleSelectorRequest;
  showInlineErrorMsg: boolean;
  isSuggestingKRInCreateKRFormEnabled: boolean;
  shouldDisplaySuggestionDrawer: boolean;
  shouldFocusTitleInput: boolean;
  shouldFocusDescriptionInput: boolean;
  drawerTitleSection: SuggestionDrawerSectionType<{ title: { isTitleAvailable: boolean } } & SuggestionDrawerSuggestionGroupType>;
  drawerDescriptionSection: SuggestionDrawerSectionType<{ description: { isDescriptionAvailable: boolean } } & SuggestionDrawerSuggestionGroupType>;
  drawerStatePropagator: SuggestionDrawerIndicatorStatePropagator;
  onInsightSelectorChange(): void;
  save(): void;
  isFormInvalid(): boolean;
  includeCritical(params: { useCritical: boolean }): void;
  selectCustomOption(option: string): void;
  updateCustomFields(customFields: CfMap): void;
  dynamicKrsFeatureEnabled(): boolean;
  setDeadline(deadline: MetricDeadLine): void;
  setDueDate(dueDate: string, type: MetricDeadLineType): void;
  setDueDateAsString(dueDate: Date, type: MetricDeadLineType): void;
  setRequiredCf(requiredCf: { areFilled: boolean }): void;
  selectMultipleOwners(ids: string[]): void;
  updateDescription(): void;
  closeModal($event?: KeyboardEvent): void;
  nameChanged(metricTitle: string, opts: { isEdit: boolean }): void;
  updateMetricField(value: RequireAtLeastOne<Metric>): void;
  edit(): void;
  isFieldPartOfWorkflow(filedName: string): boolean;
  updateMetricUnit(value: IUnitFormatting): void;
  onSuggestionDrawerVisibilityChange(isVisible: boolean): void;
  filterBySessionId(customFields: ICustomField[]): ICustomField[];
  isCustomFieldDisabled(): boolean;
}

// We don't want the saved scope to survive page refresh
let cachedNewMetric: Metric = null;
const cachedExistingMetricMap: Map<string, Metric> = new Map();
let currentMetricId: string = null;
let cacheTimestamp = Date.now();
const isCacheTimedout = (): boolean => {
  const cacheTimeout = 5 * 60 * 1000; // 5 minuts in ms
  return Date.now() - cacheTimestamp > cacheTimeout;
};

export class MetricFormCtrl implements IStateInit {
  public static $inject = [
    "$q",
    "$rootScope",
    "$scope",
    "$stateParams",
    "$timeout",
    "GoalService",
    "MetricService",
    "AccountService",
    "PermissionsService",
    "UIErrorHandlingService",
    "$ngRedux",
    "PlanningSessionsActions",
    "AutoFieldsService",
    "AnalyticsService",
    "FeatureTogglesFacade",
    "OkrMethodologyFacade",
    "AssigneeActions",
    "CurrentEmployeeActions",
    "TeamsActions",
    "QuantivePlusSuggestionsFacade",
    "PiFeatureVisibilityFacade",
    "PiSuggestionSharedMetricStateService",
    "Ng1UiModalService",
    "MetricsFacade",
  ];

  private okrEvents: OkrEvents;
  private isDeadlineChanged: boolean;
  private hasSentFeedback: boolean;
  private hasAcceptedTitleSuggestion: boolean;
  private hasAcceptedDescriptionSuggestion: boolean;
  private suggestionIndicatorFakeLoadingTimeout: IPromise<void> = null;
  private suggestionDrawerCurrentTitleSectionSuggestion: IQuantivePlusMetric;
  private suggestionDrawerCurrentDescriptionSectionSuggestion: Pick<IQuantivePlusMetric, "description">;
  private suggestedMetricFieldsNotVisibleInUI: Pick<IQuantivePlusMetric, "initialValue" | "target" | "targetOperator" | "format">;

  constructor(
    private $q: IQService,
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $scope: IMetricFormScope,
    private $stateParams: IMetricFormParams,
    private $timeout: ITimeoutService,
    private goalService: GoalService,
    private metricService: MetricService,
    private accountService: AccountService,
    private permissionsService: PermissionsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private autoFieldsService: AutoFieldsService,
    private analyticsService: AnalyticsService,
    private featureToggleService: FeatureTogglesFacade,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private assigneeActions: AssigneeActions,
    private currentEmployeeActions: CurrentEmployeeActions,
    private teamsActions: TeamsActions,
    private quantivePlusSuggestionsFacade: QuantivePlusSuggestionsFacade,
    private piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
    private piSuggestionSharedMetricStateService: PiSuggestionSharedMetricStateService,
    private modalService: Ng1UiModalService,
    private metricFacade: MetricsFacade
  ) {
    this.okrEvents = new OkrEvents(this.$scope);
    this.$scope.$on("$destroy", this.$ngRedux.connect(bindStateToScope)(this.$scope));

    this.$scope.save = this.save;
    this.$scope.isFormInvalid = this.isFormInvalid;

    this.$scope.selectMultipleOwners = this.selectMultipleOwners;

    this.$scope.metricsLimitReached = false;
    this.$scope.isEdit = false;
    this.$scope.indicators = {
      loading: { progress: true },
    };
    this.$scope.shouldPredict = {
      format: true,
      targetOperator: true,
      target: true,
      // will be set to 0 in webapp if target is predicted
      // and operator is not should decrease to
      initialValue: true,
    };

    this.$scope.dates = {
      dueDate: null,
      softDueDate: null,
    };
    this.isDeadlineChanged = false;
    this.$scope.shouldDisplayInsightSelector = this.shouldDisplayInsightSelector();
    this.$scope.shouldFocusTitleInput = true;

    this.$scope.selectCustomOption = this.selectCustomOption;
    // method used by boolean slider adjusting metric.critical and useCritical variable in view
    this.$scope.includeCritical = this.includeCritical;
    this.$scope.onInsightSelectorChange = this.onInsightSelectorChange;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.dynamicKrsFeatureEnabled = this.dynamicKrsFeatureEnabled;
    this.$scope.setDeadline = this.setDeadline;
    this.$scope.setDueDate = this.setDueDate;
    this.$scope.setRequiredCf = this.setRequiredCf;
    this.$scope.updateDescription = this.updateDescription;
    this.$scope.nameChanged = this.nameChanged;
    this.$scope.updateMetricField = this.updateMetricField;
    this.$scope.edit = this.edit;
    this.$scope.updateMetricUnit = this.updateMetricUnit;
    this.$scope.closeModal = this.closeModal;
    this.$scope.filterBySessionId = this.filterBySessionId;
    this.$scope.isCustomFieldDisabled = this.isCustomFieldDisabled;
    this.$scope.isFieldPartOfWorkflow = (fieldName) => this.isFieldPartOfWorkflow(fieldName);
    this.$scope.onSuggestionDrawerVisibilityChange = this.onSuggestionDrawerVisibilityChange;
    this.setSuggestionDrawerInitialValues();

    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    this.$scope.$on("$destroy", this.onExit);

    this.$scope.flowId = uuidv4();
    this.$scope.flowName = this.$stateParams.flowName;
  }

  stateInit(): IPromise<unknown> {
    return this.$q
      .all([
        this.checkSuggestionDrawerWhenCreateKrFeatureToggle(),
        this.$scope.watchUntilAndRejectOnError(
          () => this.$scope.reduxItemsFetched,
          () => this.$scope.reduxItemsError
        ),
      ])
      .then(
        () => this.init(),
        () => this.uiErrorHandlingService.handleModal(this.$scope.reduxItemsError)
      );
  }

  private isFieldPartOfWorkflow(fieldName: string): boolean {
    return isFieldPartOfWorkflowUtil(fieldName, this.$scope.session);
  }

  private originalMetric: Metric;
  private selectedCustomFieldOptions: string[] = [];

  private selectCustomOption = (option: string): void => {
    if (this.selectedCustomFieldOptions.indexOf(option) !== -1) {
      this.selectedCustomFieldOptions.splice(this.selectedCustomFieldOptions.indexOf(option), 1);
    } else {
      this.selectedCustomFieldOptions.push(option);
    }

    if (this.selectedCustomFieldOptions.length > 0) {
      this.$scope.isFieldRequired = false;
    }

    if (this.selectedCustomFieldOptions.length < 1) {
      this.$scope.isFieldRequired = true;
    }
  };

  private onExit = (event: IAngularEvent): void => {
    // We want to save the state of the modal only if it was closed with backdrop
    // We consider this as accidental form close and we want to save the state of the form
    const modalCloseReason = String(storage.get("modalCloseReason"));
    if (currentMetricId) {
      if (modalCloseReason && modalCloseReason.includes("backdrop")) {
        cacheTimestamp = Date.now();
        cachedExistingMetricMap[currentMetricId] = (event.targetScope as IMetricFormScope).metric;
      } else {
        cachedExistingMetricMap[currentMetricId] = null;
      }
    } else {
      if (modalCloseReason && modalCloseReason.includes("backdrop")) {
        cacheTimestamp = Date.now();
        cachedNewMetric = (event.targetScope as IMetricFormScope).metric;
      } else {
        cachedNewMetric = null;
      }
    }
  };

  private setMetricMilestoneFeatureFlag() {
    this.featureToggleService
      .isFeatureAvailable$(FeatureFlag.MetricMilestone)
      .pipe(first())
      .subscribe((isAvailable) => {
        this.$scope.isMilestoneOn = isAvailable && this.accountService.getAccountSetting("milestones");
      });
  }

  private setMetric = (metric: Metric): void => {
    if (this.$stateParams.metricId) {
      if (cachedExistingMetricMap[currentMetricId] && !isCacheTimedout()) {
        this.$scope.metric = cachedExistingMetricMap[currentMetricId];
      } else {
        this.$scope.metric = metric;
      }

      this.setEditMetricData(this.$scope.metric);
    } else {
      // if creating a new metric
      if (cachedNewMetric && !isCacheTimedout()) {
        // Load metric from local variable
        this.$scope.metric = cachedNewMetric;
      } else {
        this.setNewMetricDetails();
      }
    }

    this.$scope.useCritical = !!metric && (metric.critical !== null || metric.critical === 0);
  };

  private loadAllowedActions = (goalParams: IGetGoalParams): IPromise<unknown> => {
    if (this.$scope.goal) {
      this.setSharedMetricServiceMetric();
      // basic case
      return this.loadSessionAndAllowedActions();
    } else {
      // in case if you open edit form and refresh
      return this.goalService.getGoal(this.$scope.metric.goalId, goalParams).then((goal) => {
        this.$scope.goal = goal;

        this.setSharedMetricServiceMetric();
        return this.loadSessionAndAllowedActions();
      });
    }
  };

  private setSharedMetricServiceMetric(): void {
    const clonedMetric = angular.copy(this.$scope.metric);
    clonedMetric.sessionId = clonedMetric.sessionId || this.$scope.goal.sessionId;

    this.piSuggestionSharedMetricStateService.setMetric(clonedMetric);

    this.$scope.$evalAsync();
  }

  private init = (): IPromise<unknown> => {
    const goalParams = {
      fields: "private,id,sessionId,ownerIds,locked,workflow{status},metrics{id,name,description},name,description",
    };

    return this.$q
      .all({
        goal: this.$stateParams.id ? this.goalService.getGoal(this.$stateParams.id, goalParams) : null,
        metric: this.$stateParams.metricId ? this.metricService.getMetric(this.$stateParams.metricId) : null,
      })
      .then(
        ({ goal, metric }) => {
          if (this.$stateParams.metricId) {
            this.metricFacade
              .getMetric$(this.$stateParams.metricId, ["milestones"])
              .pipe(takeUntil(new Ng1DestroyService(this.$scope)))
              .subscribe((metric) => {
                this.$scope.metric.milestones = metric.milestones;
                if (this.$scope.metric.milestones?.length > 0) {
                  this.setLastMilestone(this.$scope.metric.milestones[0]);
                }
              });
          }

          this.setMetricMilestoneFeatureFlag();

          if (this.$stateParams.id) {
            this.$scope.goal = goal;
            this.setSuggestionDrawerSectionsImproveSuggestionContext();
          }

          currentMetricId = this.$stateParams.metricId;

          this.setMetric(metric);
          this.setSuggestionIndicatorFakeLoadingState();

          return this.loadAllowedActions(goalParams);
        },
        (error) => (this.$scope.indicators.loading = { error: error })
      );
  };

  private setMetricOwnerIds = (metric: Metric): void => {
    if (this.$scope.isEdit) {
      return;
    }

    const metricOwnersPermissions: IMetricMethodologySettingsUI = {
      assignMetricPermissions: this.$scope.metricMethodologySettings.assignMetricPermissions,
      multipleOwnersPerMetricDisabled: this.$scope.metricMethodologySettings.multipleOwnersPerMetricDisabled,
    };

    metric.ownerIds = this.filterOwnerIdsByMetricMethodologySettings(metricOwnersPermissions, this.$scope.goal.ownerIds);

    this.setSharedMetricServiceMetric();
  };

  private loadSessionAndAllowedActions(): IPromise<unknown> {
    return this.$q
      .all({
        allowedActions: this.$stateParams.metricId
          ? this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$scope.metric.id, "metric")
          : this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$scope.goal.id, "goal"),
        trackMetricForm: this.metricService.trackMetricForm(MetricFormEventType.KR_FORM_OPENED, this.$scope.metric),
      })
      .then(({ allowedActions }) => {
        delete this.$scope.indicators.loading;

        this.$scope.session = this.$scope.goal.sessionId && this.$scope.goal.sessionId !== "" ? this.$scope.sessionIdMap[this.$scope.goal.sessionId] : null;
        this.$scope.allowedActions = allowedActions.currentUserActions;

        disableDatesOutsideSessionTimeframe(this.$scope, this.$stateParams);
        this.setMetricMethodologySettings();
        this.setPeopleSelectorRequest();
        this.checkMetricsLimit();

        if (this.$scope.shouldDisplayInsightSelector) {
          this.$scope.insightSelector = { data: generateInsightSelectorData(this.$scope.metric) };
        }

        this.$scope.$broadcast("focusTextInput");
      });
  }

  private setMetricMethodologySettings(): void {
    this.$scope.metricMethodologySettings = {
      ...this.okrMethodologyFacade.getMethodologySettingsUIForMetricView(this.$scope.session),
      metricsPerGoalLimit: this.okrMethodologyFacade.getLimitSetting("metric", this.$scope.session?.settings),
    };

    this.setMetricOwnerIds(this.$scope.metric);
  }

  private setEditMetricData = (metric: Metric) => {
    if (metric.dueDate === metricFormStrings.default.date) {
      this.$scope.useDeadline = MetricDeadLineEnum.NONE;
    } else if (metric.softDueDate === metricFormStrings.default.date) {
      this.$scope.useDeadline = MetricDeadLineEnum.HARD_DEADLINE;
    } else {
      this.$scope.useDeadline = MetricDeadLineEnum.HARD_AND_SOFT_DEADLINE;
    }

    // needed when editing metric, originalMetricType is used in the view
    this.$scope.originalMetricType = metric.manualType;

    // needed when editing metric to send only edited fields via patch metric http request (if fields are objects, they are always sent with patch)
    this.originalMetric = angular.copy(metric);
    this.$scope.isEdit = true;
  };

  private filterOwnerIdsByMetricMethodologySettings = (permissions: IMetricMethodologySettingsUI, goalOwnerIds: string[]): string[] => {
    let metricOwnerIds = goalOwnerIds || [];

    if (permissions.assignMetricPermissions === "team") {
      metricOwnerIds = goalOwnerIds.filter((id) => this.$scope.assigneesIdMap[id].type === "team");
    }

    if (permissions.assignMetricPermissions === "user") {
      metricOwnerIds = goalOwnerIds.filter((id) => this.$scope.assigneesIdMap[id].type === "user");
    }

    return permissions.multipleOwnersPerMetricDisabled ? [metricOwnerIds[0]] : metricOwnerIds;
  };

  private setNewMetricDetails = () => {
    this.$scope.useDeadline = MetricDeadLineEnum.NONE;
    this.$scope.metric = this.generateDefaultMetricValues();
  };

  private generateDefaultMetricValues = (): Metric => {
    return {
      ownerIds: this.$scope.goal.ownerIds,
      goalId: this.$stateParams.id,
      manualType: metricFormStrings.double,
      targetOperator: "at_least",
      initialValue: null,
      target: null,
      // setting critical to null when creating a new metric
      critical: null,
      name: "",
      dueDate: null,
      softDueDate: null,
      links: <Links<"metric">>{},
      customFields: {},
      format: {
        prefix: "",
        suffix: "",
        fractionSize: 0,
        isCustomUnit: false,
      },
      insightName: "",
    } as Metric;
  };

  private checkMetricsLimit = (): void => {
    const metricsCount = this.$scope.goal.metrics?.length || -1;

    if (this.$scope.isEdit) {
      this.$scope.metricMethodologySettings.metricsPerGoalLimit = DEFAULT_LIMIT;
    }

    this.$scope.metricsLimitReached = metricsCount >= this.$scope.metricMethodologySettings.metricsPerGoalLimit;
  };

  // TODO actually this method always receives a Date Object as an argument - from both old and new date pickers
  private setDueDate = (dueDate: string, type: MetricDeadLineType): void => {
    switch (type) {
      case "softDeadline":
        this.$scope.dates.softDueDate = dueDate;
        break;
      case "hardDeadline":
        this.$scope.dates.dueDate = dueDate;
        if (dayjs(this.$scope.dates.dueDate).isSameOrBefore(dayjs(this.$scope.dates.softDueDate))) {
          this.$scope.dates.softDueDate = dayjs(this.$scope.dates.dueDate).subtract(1, metricFormStrings.day).toISOString();
        }
        break;
    }
    this.$scope.$evalAsync();
  };

  private setDeadline = (deadline: MetricDeadLine): void => {
    this.$scope.useDeadline = deadline;
    this.isDeadlineChanged = true;
  };

  private edit = () => {
    if (this.shouldOpenDeadlineWarningModal()) {
      this.openWarningModal();
    } else {
      this.save();
    }
  };

  private openWarningModal = () => {
    this.modalService.confirm({
      uiTitle: localize("are_you_sure_you_want_to_change_the_deadline"),
      uiContent: localize("changing_the_deadline_of_cascading_key_result"),
      uiOkText: localize("change"),
      uiOnOk: () => this.save(),
      uiOnCancel: () => restoreMetricDueDates(this.$scope),
    });
  };

  private shouldOpenDeadlineWarningModal = (): boolean => {
    return (
      this.isCascadingMetric() &&
      (this.areDatesChanged(this.$scope.metric.dueDate, this.$scope.dates.dueDate) ||
        this.areDatesChanged(this.$scope.metric.softDueDate, this.$scope.dates.softDueDate) ||
        this.isDeadlineChanged)
    );
  };

  private isCascadingMetric = (): boolean => {
    return !!this.$scope.metric.cascadeType.length;
  };

  private areDatesChanged = (metricDueDate: string, scopeDueDate: string): boolean => {
    const areDueDatesDifferent: boolean = metricDueDate !== scopeDueDate;
    return (
      (areDueDatesDifferent && metricDueDate === metricFormStrings.default.date && this.isDeadlineChanged) ||
      (areDueDatesDifferent && metricDueDate !== metricFormStrings.default.date)
    );
  };

  private sendRecommendationFeedback = (metric: Metric): void => {
    if (this.$scope.prediction) {
      this.sendPredictionAnalyticsData(metric);
    }

    if (this.$scope.insightSelector?.data) {
      sendPredictionAnalyticsFeedback(this.autoFieldsService, this.featureToggleService, this.$scope.insightSelector.data);
      sendTrackingAnalytics(this.analyticsService, this.$scope.insightSelector.data, "Key Result Insight Suggestion Created");
    }
  };

  private createMetric = () => {
    const shouldCheckin = this.$scope.metric.actual !== this.$scope.metric.initialValue;
    const gtmhubAdditionalParams = this.generateAdditionalPiReqParams();

    this.metricService.createMetric(this.$scope.metric, gtmhubAdditionalParams).then(
      (metric) => {
        if (this.$scope.isMilestoneOn) {
          this.metricService.trackMetricMilestone(MetricMilestoneEventType.KR_MILESTONES_CREATED, { metric });
        }

        if (shouldCheckin) {
          const checkin: ICheckin = {
            checkInDate: new Date().toISOString(),
            actual: this.$scope.metric.actual,
            confidence: this.$scope.metric.confidence?.value,
            customFields: this.$scope.metric.customFields,
          };
          this.metricService.checkin(metric, checkin).then(() => {
            this.okrEvents.broadcastGoalsChanged("metricSnapshotCreated", {
              checkin,
              metric: this.$scope.metric,
              goal: this.$scope.goal,
              session: this.$scope.session,
            });
          });
        }

        this.sendRecommendationFeedback(metric);
        this.trackRecommendationHelpfulness(metric);
        this.afterSave();
      },
      (error) => (this.$scope.indicators.saving = { error: error })
    );
  };

  private save = () => {
    this.$scope.indicators.saving = { progress: true };

    setMetricDueDates(this.$scope);

    // setting metric.critical as null if not creating anti-target KR
    this.setMetricCriticalTarget(this.$scope.metric);

    if (!this.$scope.metric.ownerIds) {
      this.$scope.metric.ownerIds = this.$scope.goal.ownerIds;
    }

    parseDecimalInput(this.$scope.metric, ["actual", "initialValue", "target"]);

    if (this.$scope.isEdit) {
      this.$rootScope.traceAction("update_KR", () => {
        this.updateMetric();
      });
    } else {
      this.$rootScope.traceAction("create_KR", () => {
        // setting new boolean initial to 0, target to 1, targetOperator to "at_least", prefix and suffix to ""
        this.setBooleanValues(this.$scope.metric.manualType, "at_least", 0, 1);

        if (this.$scope.metric.actual === null || this.$scope.metric.actual === undefined) {
          this.$scope.metric.actual = this.$scope.metric.initialValue;
        }
        this.createMetric();
      });
    }
  };

  private trackRecommendationHelpfulness(metric: Metric): void {
    // recommendation is helpful if user doesn’t remove themselves as owner
    this.analyticsService.track(recommendation, {
      is_useful: metric.ownerIds.includes(getCurrentUserId()),
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_metric;owner",
    });
  }

  private updateMetric = () => {
    // if metric type is changed
    if (this.$scope.originalMetricType !== this.$scope.metric.manualType) {
      // if changing type from number to boolean -> initial to 0, target to 1, targetOperator to "at_least", prefix and suffix to ""
      this.setBooleanValues(this.$scope.metric.manualType, "at_least", 0, 1);

      // setting actual value to initial value if manual type is changed
      // this logic could be removed when backend has implemented such logic when patching metric
      this.$scope.metric.actual = this.$scope.metric.initialValue;

      // else if metric type was not changed
      // but initial value is changed
    } else {
      // when editing number KRs if not checked in yet
      // if changing initial value -> change the actual value as well
      if (
        (this.$scope.metric.manualType === metricFormStrings.double || this.$scope.metric.manualType === metricFormStrings.empty) &&
        this.$scope.metric.metricHistory.length === 0 &&
        this.originalMetric.initialValue !== this.$scope.metric.initialValue
      ) {
        // making actual value equal to the new initial value
        this.$scope.metric.actual = this.$scope.metric.initialValue;
      }
    }

    const updatedFields = this.createPartialMetric(this.$scope.metric);
    const keys = Object.keys(updatedFields);
    if (keys.length === 1 && keys[0] === "id") {
      this.$scope.$dismiss();
      return;
    }

    // patching KR changes
    this.metricService.patchMetric(updatedFields).then(
      () => {
        this.afterSave();
      },
      (error) => (this.$scope.indicators.saving = { error: error })
    );
  };

  private createPartialMetric = (metric: Metric): Partial<Metric> => {
    const partialMetric: Partial<Metric> = {
      id: metric.id,
    };

    let properties = metricProperties;
    if (this.$scope.goal.locked && this.$scope.session.workflow?.editableKrFields?.length > 0) {
      properties = filterWorkflowSelectionProperties(this.$scope.session.workflow.editableKrFields);
    }

    properties.forEach((property) => {
      if (!equal(metric[property], this.originalMetric[property])) {
        partialMetric[property] = metric[property];
      }
    });

    return partialMetric;
  };

  // dealing with boolean stuff
  // and adjusting targetOperator if changing type to boolean from number
  // boolean targetOperator for boolean metrics is always "at_least"
  private setBooleanValues = (manualType: string, targetOperator: MetricTargetOperator, initialValue: number, target: number) => {
    if (manualType === metricFormStrings.boolean) {
      this.$scope.metric.initialValue = initialValue;
      this.$scope.metric.target = target;

      if (this.$scope.metric.format) {
        this.$scope.metric.format.prefix = "";
        this.$scope.metric.format.suffix = "";
      }

      // boolean metrics targetOperator is always "at_least"
      this.$scope.metric.targetOperator = targetOperator ? targetOperator : "at_least";
    }
  };

  private setMetricCriticalTarget = (metric: Metric): void => {
    if (
      (metric.manualType === metricFormStrings.double || metric.manualType === metricFormStrings.empty) &&
      (metric.targetOperator === "should_stay_above" || metric.targetOperator === "should_stay_below")
    ) {
      return;
    }

    // if metric is not anti-target KR --> set critical value to null
    this.$scope.metric.critical = null;
  };

  private afterSave = () => {
    delete this.$scope.indicators.saving;

    this.okrEvents.broadcastGoalsChanged("metricUpdated", {
      metric: this.$scope.metric,
      goal: this.$scope.goal,
      session: this.$scope.sessionIdMap[this.$scope.goal.sessionId],
    });

    // displaying goal and goals changes
    this.okrEvents.broadcastGoalsChanged("goalChanged");
    this.$rootScope.$broadcast("goalsChanged");

    this.$scope.$dismiss();
  };

  // ui validator not allowing to set "negative" attainment metrics
  private validateDoubleInputValues = (): void => {
    const manualType: string = this.$scope.metric.manualType;
    const targetOperator: MetricTargetOperator = this.$scope.metric.targetOperator;
    const initialValue: number = this.$scope.metric.initialValue;
    const targetValue: number = this.$scope.metric.target;
    const criticalValue: number = this.$scope.metric.critical;

    const isManualTypeDoubleOrEmpty = manualType === metricFormStrings.double || manualType === metricFormStrings.empty;
    const shouldNotShowErr = this.$scope.useCritical ? targetValue === null || criticalValue === null : initialValue === null || targetValue === null;

    if (isManualTypeDoubleOrEmpty && shouldNotShowErr) {
      this.$scope.errorMessage = null;
      this.$scope.showInlineErrorMsg = false;

      return;
    }

    if (isManualTypeDoubleOrEmpty && targetOperator === "at_least" && initialValue >= targetValue) {
      this.$scope.errorMessage = "initial_lower_than_target";
      this.$scope.errorMessageId = "initial-lower-than-target";
      this.$scope.showInlineErrorMsg = true;

      return;
    }

    if (isManualTypeDoubleOrEmpty && targetOperator === "at_most" && initialValue <= targetValue) {
      this.$scope.errorMessage = "initial_greater_than_target";
      this.$scope.errorMessageId = "initial-greater-than-target";
      this.$scope.showInlineErrorMsg = true;

      return;
    }

    if (
      isManualTypeDoubleOrEmpty &&
      targetOperator === "should_stay_above" &&
      criticalValue !== null &&
      typeof criticalValue !== "undefined" &&
      criticalValue >= targetValue
    ) {
      this.$scope.errorMessage = "critical_lower_than_target";
      this.$scope.errorMessageId = "critical-lower-than-target";
      this.$scope.showInlineErrorMsg = true;

      return;
    }

    if (
      isManualTypeDoubleOrEmpty &&
      targetOperator === "should_stay_below" &&
      criticalValue !== null &&
      typeof criticalValue !== "undefined" &&
      criticalValue <= targetValue
    ) {
      this.$scope.errorMessage = "critical_greater_than_target";
      this.$scope.errorMessageId = "critical-greater-than-target";
      this.$scope.showInlineErrorMsg = true;

      return;
    }

    this.$scope.showInlineErrorMsg = false;
  };

  private includeCritical = (params: { useCritical: boolean }): void => {
    if (params.useCritical) {
      // setting metric.critical to be null when critical target is not used
      this.$scope.metric.critical = null;

      this.$scope.useCritical = false;
    } else {
      this.$scope.useCritical = true;
    }

    this.$scope.$evalAsync();
  };

  private onInsightSelectorChange = (): void => {
    this.$scope.metric = this.metricService.updateMetricInsightData(this.$scope.metric, this.$scope.insightSelector.data, this.$scope.shouldPredict);
    this.validateDoubleInputValues();
  };

  private updateCustomFields = (customFields: CfMap) => {
    this.$scope.metric.customFields = customFields;
    this.$scope.$evalAsync();
  };

  private dynamicKrsFeatureEnabled = (): boolean => this.accountService.featureAvailable("insightboards.dynamic-kr");
  private autoFieldsFeatureEnabled = (): boolean => this.accountService.featureAvailable("autofields");

  private shouldDisplayInsightSelector = (): boolean =>
    this.dynamicKrsFeatureEnabled() && (this.$rootScope.hasPermission(metricFormStrings.manageData) || this.$rootScope.hasRole(metricFormStrings.admin));

  private setRequiredCf = (requiredCf: { areFilled: boolean }): void => {
    this.$scope.requiredCfNotEmpty = requiredCf.areFilled;
  };

  private selectMultipleOwners = (ids: string[]): void => {
    if (!ids.length) {
      return;
    }

    this.$scope.metric.ownerIds = ids;

    this.setSharedMetricServiceMetric();
  };

  private updateDescription = () => {
    this.reflectChangesInSuggestionDrawerDescriptionSection();
    this.$scope.shouldFocusDescriptionInput = false;

    this.$scope.$evalAsync();

    this.trackPiSuggestionEdited();

    this.setSharedMetricServiceMetric();
  };

  private nameChanged = (metricTitle: string, opts: { isEdit: boolean }): void => {
    this.$scope.metric.name = metricTitle;
    this.setSharedMetricServiceMetric();

    if (this.$scope.metric.name === "") {
      this.reflectChangesInAllSuggestionDrawerSections();

      return;
    }

    this.setAutopopulatedFields(metricTitle, opts);
    this.reflectChangesInSuggestionDrawerTitleSection();
    this.reflectChangesInSuggestionDrawerDescriptionSection();
    this.trackPiSuggestionEdited();
  };

  private setAutopopulatedFields = (metricTitle: string, opts: { isEdit: boolean }): void => {
    this.$rootScope.$broadcast("searchInsightSuggestions", { name: metricTitle });

    if (opts.isEdit) {
      return;
    }

    const noFieldsShouldBePredicted = Object.values(this.$scope.shouldPredict).every((v) => !v);
    if (noFieldsShouldBePredicted || this.$scope.metric.targetOperator === "boolean") {
      return;
    }

    if (this.autoFieldsFeatureEnabled()) {
      this.autoFieldsService
        .getPredictionFromMetric(metricTitle)
        .then((prediction) => {
          this.$scope.prediction = prediction;
          this.updateAllPredictedMetrics(prediction);
        })
        .catch(() => {
          this.$scope.prediction = null;
        });
    }
  };

  private updateAllPredictedMetrics = (prediction: IMetricPrediction): void => {
    for (const [field, shouldPredict] of Object.entries(this.$scope.shouldPredict)) {
      if (shouldPredict) {
        const defaultFieldValue = this.generateDefaultMetricValues()[field];
        this.$scope.metric[field] = prediction[field] || defaultFieldValue || null;
      }
    }

    const shouldPredictInitialValue =
      this.$scope.shouldPredict.target && this.$scope.shouldPredict.initialValue && this.$scope.metric.targetOperator !== "at_most" && prediction.target;

    if (shouldPredictInitialValue) {
      this.$scope.prediction.initialValue = 0;
      this.$scope.metric.initialValue = 0;
    }
  };

  private updateMetricUnit = (value: IUnitFormatting): void => {
    this.$scope.updateMetricField({ format: value });
  };

  private updateMetricField = (updatedField: RequireAtLeastOne<Metric>) => {
    if (updatedField.milestones?.length > 0) {
      this.setLastMilestone(updatedField.milestones[0]);

      updatedField.milestones = updatedField.milestones.filter((milestone) => {
        return milestone.targetValue !== null && typeof milestone.targetValue !== "undefined" && milestone.date !== null && typeof milestone.date !== "undefined";
      });
    }

    if (updatedField.target || updatedField.targetOperator) {
      this.validateMilestoneValue(updatedField.target || this.$scope.metric.target, updatedField.targetOperator || this.$scope.metric.targetOperator);
    }

    this.$scope.metric = { ...this.$scope.metric, ...updatedField };

    const predictedKey = MetricPredictedFields.find((key) => key in updatedField);
    if (predictedKey) {
      this.disablePredictionForField(predictedKey);
    }

    if (CreateKrSuggestionDrawerLegacyComponent.suggestedMetricPropsWithoutSuggestionCardEquivalentInUI.find((key) => key in updatedField)) {
      this.trackPiSuggestionEdited();
    }

    this.validateDoubleInputValues();

    this.$scope.$evalAsync();
  };

  private validateMilestoneValue = (target: number, targetOperator: MetricTargetOperator): void => {
    const targetValue = this.$scope.lastMilestone?.targetValue;

    if (!targetValue) {
      return;
    }

    if (targetOperator === "at_least" && targetValue > target) {
      this.$scope.milestoneErrorMessageId = "milestone_greater_than_target";
      return;
    }

    if (targetOperator === "at_most" && targetValue < target) {
      this.$scope.milestoneErrorMessageId = "milestone_less_than_target";
      return;
    }

    this.$scope.milestoneErrorMessageId = null;
  };

  private setLastMilestone = (milestone: Milestone): void => {
    if (!milestone?.targetValue && !milestone?.date) {
      this.$scope.lastMilestone = null;
      return;
    }

    this.$scope.lastMilestone = milestone;
  };

  private disablePredictionForField = (field: MetricPredictedFields) => {
    this.$scope.shouldPredict[field] = false;
  };

  private sendPredictionAnalyticsData = (newMetric: Metric): void => {
    const data = generateMetricPredictionAnalytics(newMetric, this.$scope.prediction);
    this.autoFieldsService.sendMetricAnalyticsData(data);
  };

  private setPeopleSelectorRequest(): void {
    this.$scope.peopleSelectorRequest = {
      ...new PeopleSelectorRequest(),
      type: this.$scope.metricMethodologySettings.assignMetricPermissions,
      permissionSetting: { enabled: true, permission: "ManageGoals" },
    };
  }

  private isFormInvalid = (): boolean => {
    const isMissingRequiredValueFields =
      this.$scope.metric.manualType !== "boolean" && (typeof this.$scope.metric.target !== "number" || typeof this.$scope.metric.initialValue !== "number");
    const hasInvalidInputValues = this.$scope.showInlineErrorMsg || isMissingRequiredValueFields;

    return hasInvalidInputValues || !this.$scope.requiredCfNotEmpty || !this.$scope.metric.name;
  };

  private setSuggestionDrawerInitialValues(): void {
    this.$scope.drawerTitleSection = {
      isLoading: false,
      hasError: false,
      isHidden: false,
      errorData: getPiErrorData("title", null),
      hasAllDependencies: true,
      noSuggestionReason: "",
      suggestions: [{ id: "", suggestionText: "" }],
      title: {
        isTitleAvailable: true,
      },
      improveSuggestionContext: {
        parentId: "",
        parentType: "",
        entityId: "",
      },
      onAcceptSuggestion: (suggestion: { id: string; suggestionText: string }) => this.onTitleSuggestionAccepted(suggestion),
      onSuggestAnother: () => this.getTitleSuggestion({ isRefresh: true }),
      onRetry: () => this.getTitleSuggestion(),
      goBackToLastAvailableSuggestion: () => this.showLastAvailableTitleSuggestion(),
    };

    this.$scope.drawerDescriptionSection = {
      isLoading: false,
      hasError: false,
      isHidden: false,
      errorData: getPiErrorData("description", null),
      hasAllDependencies: true,
      noSuggestionReason: "",
      suggestions: [{ id: "", suggestionText: "" }],
      description: {
        isDescriptionAvailable: true,
      },
      improveSuggestionContext: {
        parentId: "",
        parentType: "",
        entityId: "",
      },
      onAcceptSuggestion: () => this.setAcceptedDescriptionSuggestion(),
      onSuggestAnother: () => this.getDescriptionSuggestion({ isRefresh: true }),
      onRetry: () => this.getDescriptionSuggestion(),
      goBackToLastAvailableSuggestion: () => this.showLastAvailableDescriptionSuggestion(),
    };

    this.$scope.drawerStatePropagator = new SuggestionDrawerIndicatorStatePropagator();
  }

  private setSuggestionDrawerSectionsImproveSuggestionContext = (): void => {
    const improveSuggestionContext: PIImproveSuggestionContext = {
      entityId: this.$scope.metric?.id,
      parentId: this.$scope.goal.id,
      parentType: "objective",
    };

    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      improveSuggestionContext,
    };

    this.$scope.drawerDescriptionSection = {
      ...this.$scope.drawerDescriptionSection,
      improveSuggestionContext,
    };
  };

  private updateSuggestionDrawerTitleSectionBasedOnDependencies = (): void => {
    const isTitleFilledIn = !!this.$scope.metric.name;

    if (isTitleFilledIn) {
      this.$scope.drawerTitleSection = {
        ...this.$scope.drawerTitleSection,
        hasAllDependencies: false,
        isLoading: false,
        noSuggestionReason: CreateKrSuggestionDrawerNoSuggestionReasonsMap.YOUR_KEY_RESULT_ALREADY_HAS_A_TITLE,
        title: { isTitleAvailable: false },
      };
    } else {
      this.$scope.drawerTitleSection = {
        ...this.$scope.drawerTitleSection,
        hasAllDependencies: true,
        title: { isTitleAvailable: true },
      };
    }
  };

  private reflectChangesInSuggestionDrawerTitleSection = (): void => {
    this.updateSuggestionDrawerTitleSectionBasedOnDependencies();

    if (!this.$scope.shouldDisplaySuggestionDrawer) {
      this.setSuggestionIndicatorFakeLoadingState();

      return;
    }

    const isTitleFilledIn = !!this.$scope.metric.name;
    if (isTitleFilledIn) return;

    this.getTitleSuggestion();
  };

  private reflectChangesInAllSuggestionDrawerSections = (): void => {
    this.updateSuggestionDrawerTitleSectionBasedOnDependencies();
    this.updateSuggestionDrawerDescriptionSectionBasedOnDependencies();

    if (!this.$scope.shouldDisplaySuggestionDrawer) {
      this.setSuggestionIndicatorFakeLoadingState();

      return;
    }

    const isTitleFilledIn = !!this.$scope.metric.name;
    const isDescriptionFilledIn = !!this.$scope.metric.description;

    if (isTitleFilledIn && isDescriptionFilledIn) return;

    if (isTitleFilledIn) {
      this.getDescriptionSuggestion({ isRefresh: false });

      return;
    }

    this.getTitleSuggestion();
  };

  private updateSuggestionDrawerDescriptionSectionBasedOnDependencies = (): void => {
    const isDescriptionFilledIn = !!this.$scope.metric.description;
    const isTitleFilledIn = !!this.$scope.metric.name;

    if (isDescriptionFilledIn) {
      this.$scope.drawerDescriptionSection = {
        ...this.$scope.drawerDescriptionSection,
        hasAllDependencies: false,
        isLoading: false,
        noSuggestionReason: CreateKrSuggestionDrawerNoSuggestionReasonsMap.YOUR_KEY_RESULT_ALREADY_HAS_A_DESCRIPTION,
        description: { isDescriptionAvailable: false },
      };
    } else if (!isTitleFilledIn) {
      this.$scope.drawerDescriptionSection = {
        ...this.$scope.drawerDescriptionSection,
        hasAllDependencies: false,
        isLoading: false,
        noSuggestionReason: CreateKrSuggestionDrawerNoSuggestionReasonsMap.ENTER_TITLE_TO_VIEW_SUGGESTIONS,
        description: { isDescriptionAvailable: false },
      };
    } else {
      this.$scope.drawerDescriptionSection = {
        ...this.$scope.drawerDescriptionSection,
        hasAllDependencies: true,
        description: { isDescriptionAvailable: true },
      };
    }
  };

  private reflectChangesInSuggestionDrawerDescriptionSection = (): void => {
    this.updateSuggestionDrawerDescriptionSectionBasedOnDependencies();

    if (!this.$scope.shouldDisplaySuggestionDrawer) {
      this.setSuggestionIndicatorFakeLoadingState();

      return;
    }

    const isDescriptionFilledIn = !!this.$scope.metric.description;
    if (isDescriptionFilledIn) return;

    this.getDescriptionSuggestion();
  };

  private checkSuggestionDrawerWhenCreateKrFeatureToggle = (): void => {
    this.piFeatureVisibilityFacade
      .isSuggestingKRInCreateKRFormVisible$()
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.shouldDisplaySuggestionDrawer = false;
          this.$scope.isSuggestingKRInCreateKRFormEnabled = enabled;
        },
      });
  };

  private onSuggestionDrawerVisibilityChange = (isVisible: boolean): void => {
    this.$scope.shouldDisplaySuggestionDrawer = isVisible;
    this.$scope.$evalAsync();

    if (this.$scope.shouldDisplaySuggestionDrawer) {
      this.initiatePiFlowIfNeeded();
      this.reflectChangesInAllSuggestionDrawerSections();
    }
  };

  private setSuggestionIndicatorFakeLoadingState = (): void => {
    if (!this.$scope.isSuggestingKRInCreateKRFormEnabled) return;

    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      isLoading: true,
    };
    this.$scope.drawerDescriptionSection = {
      ...this.$scope.drawerDescriptionSection,
      isLoading: true,
    };

    this.$scope.$evalAsync();

    const suggestionIndicatorFakeLoadingDelay = 2000;

    this.$timeout.cancel(this.suggestionIndicatorFakeLoadingTimeout);

    this.suggestionIndicatorFakeLoadingTimeout = this.$timeout(() => {
      if (!this.$scope.shouldDisplaySuggestionDrawer) {
        this.$scope.drawerTitleSection = {
          ...this.$scope.drawerTitleSection,
          isLoading: false,
        };
        this.$scope.drawerDescriptionSection = {
          ...this.$scope.drawerDescriptionSection,
          isLoading: false,
        };

        this.$scope.$evalAsync();
      }
    }, suggestionIndicatorFakeLoadingDelay);
  };

  private getTitleSuggestion = (args: { isRefresh?: boolean } = {}): void => {
    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      isLoading: true,
      hasError: false,
    };

    this.$scope.$evalAsync();

    this.getKrSuggestion({ isRefresh: args.isRefresh, rejectedKrs: [this.suggestionDrawerCurrentTitleSectionSuggestion] })
      .pipe(take(1))
      .subscribe({
        next: (suggestion) => {
          this.suggestedMetricFieldsNotVisibleInUI = pick(
            suggestion.suggestions.keyresults[0],
            CreateKrSuggestionDrawerLegacyComponent.suggestedMetricPropsWithoutSuggestionCardEquivalentInUI
          );

          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: false,
            suggestions: [
              {
                id: suggestion.suggestions.keyresults[0].id,
                suggestionText: suggestion.suggestions.keyresults[0].name,
              },
            ],
          };

          this.suggestionDrawerCurrentTitleSectionSuggestion = suggestion.suggestions.keyresults[0];

          this.$scope.$evalAsync();
        },
        error: (err: HttpErrorResponse) => {
          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("title", err),
          };

          this.$scope.$evalAsync();
        },
      });
  };

  private getDescriptionSuggestion = (args: { isRefresh?: boolean } = {}): void => {
    this.$scope.drawerDescriptionSection = {
      ...this.$scope.drawerDescriptionSection,
      isLoading: true,
      hasError: false,
    };

    this.$scope.$evalAsync();

    this.getKrDescriptionSuggestion({ isRefresh: args.isRefresh, rejectedKrDescriptionSuggestion: [this.suggestionDrawerCurrentDescriptionSectionSuggestion] })
      .pipe(take(1))
      .subscribe({
        next: (suggestion) => {
          this.$scope.drawerDescriptionSection = {
            ...this.$scope.drawerDescriptionSection,
            isLoading: false,
            hasError: false,
            suggestions: [
              {
                id: suggestion.suggestions.id,
                suggestionText: suggestion.suggestions.description,
              },
            ],
          };

          this.suggestionDrawerCurrentDescriptionSectionSuggestion = { description: suggestion.suggestions.description };

          this.$scope.$evalAsync();
        },
        error: (err: HttpErrorResponse) => {
          this.$scope.drawerDescriptionSection = {
            ...this.$scope.drawerDescriptionSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("description", err),
          };

          this.$scope.$evalAsync();
        },
      });
  };

  private getKrSuggestion = (params: { isRefresh: boolean; rejectedKrs?: IQuantivePlusMetric[] }) => {
    const metrics = [...(this.$scope.goal.metrics || [])];
    const existingKrs = mapMetricsToPiExistingKrs(metrics);

    const payload = generateQuantivePlusSuggestionsPayload({
      subEntityType: "createMetricTitle",
      goal: this.$scope.goal,
      assigneesMap: this.$scope.assigneesIdMap,
      existingKrs: existingKrs,
      teams: this.$scope.teams,
      nSuggestions: 1,
      ...(params.isRefresh && {
        isRefresh: true,
        rejectedKrs: params.rejectedKrs,
      }),
    });

    return this.quantivePlusSuggestionsFacade.getQuantivePlusKrSuggestion({ ...payload, flowId: this.$scope.flowId });
  };

  private getKrDescriptionSuggestion = (params: { isRefresh?: boolean; rejectedKrDescriptionSuggestion?: Pick<IQuantivePlusMetric, "description">[] }) => {
    const payload = generateQuantivePlusKeyResultDescriptionSuggestionPayload({
      entityId: this.$scope.goal.id,
      subEntityType: "createMetricDescription",
      metric: this.$scope.metric,
      assigneesMap: this.$scope.assigneesIdMap,
      teams: this.$scope.teams,
      ...(params.isRefresh && {
        isRefresh: true,
        rejectedDescriptions: params.rejectedKrDescriptionSuggestion,
      }),
    });

    return this.quantivePlusSuggestionsFacade.getKeyResultDescriptionSuggestion({ ...payload, flowId: this.$scope.flowId });
  };

  private onTitleSuggestionAccepted = (suggestion: { id: string; suggestionText: string }): void => {
    this.setAcceptedTitleSuggestion(suggestion);
    this.applySuggestedFieldValuesNotAvailableInUI();
    this.reflectChangesInSuggestionDrawerDescriptionSection();
    this.trackPiSuggestionAccepted(this.$scope.drawerTitleSection.suggestions[0].id);
  };

  private setAcceptedTitleSuggestion = (suggestion: { id: string; suggestionText: string }): void => {
    this.$scope.metric.name = suggestion.suggestionText;
    this.$scope.shouldFocusTitleInput = true;

    this.$scope.$evalAsync();

    this.hasAcceptedTitleSuggestion = true;
    this.reflectChangesInSuggestionDrawerTitleSection();

    /** fetch relevant insights when we dynamically change metric name */
    this.$rootScope.$broadcast("searchInsightSuggestions", { name: suggestion.suggestionText });
  };

  private showLastAvailableTitleSuggestion = (): void => {
    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      hasError: false,
      errorData: getPiErrorData("title", null),
    };

    this.$scope.$evalAsync();
  };

  private applySuggestedFieldValuesNotAvailableInUI = (): void => {
    const actualMetricValuesForSuggestedKeys = pick(this.$scope.metric, CreateKrSuggestionDrawerLegacyComponent.suggestedMetricPropsWithoutSuggestionCardEquivalentInUI);
    const defaultMetricValuesForSuggestedKeys = pick(
      this.generateDefaultMetricValues(),
      CreateKrSuggestionDrawerLegacyComponent.suggestedMetricPropsWithoutSuggestionCardEquivalentInUI
    );
    const shouldSkipSuggestedValues = !matches(actualMetricValuesForSuggestedKeys)(defaultMetricValuesForSuggestedKeys);

    if (shouldSkipSuggestedValues) return;

    this.$scope.metric.targetOperator = this.suggestedMetricFieldsNotVisibleInUI.targetOperator;
    this.$scope.metric.format = this.suggestedMetricFieldsNotVisibleInUI.format;
    this.$scope.metric.target = this.suggestedMetricFieldsNotVisibleInUI.target;
    this.$scope.metric.initialValue = this.suggestedMetricFieldsNotVisibleInUI.initialValue;
  };

  private setAcceptedDescriptionSuggestion = (): void => {
    this.$scope.metric.description = this.$scope.drawerDescriptionSection.suggestions[0].suggestionText;
    this.$scope.shouldFocusDescriptionInput = true;

    this.$scope.$evalAsync();

    this.hasAcceptedDescriptionSuggestion = true;
    this.reflectChangesInSuggestionDrawerDescriptionSection();
    this.trackPiSuggestionAccepted(this.$scope.drawerDescriptionSection.suggestions[0].id);
  };

  private showLastAvailableDescriptionSuggestion = (): void => {
    this.$scope.drawerDescriptionSection = {
      ...this.$scope.drawerDescriptionSection,
      hasError: false,
      errorData: getPiErrorData("description", null),
    };

    this.$scope.$evalAsync();
  };

  private initiatePiFlowIfNeeded = (): void => {
    /** we only send the flow initiated event once */
    if (this.hasSentFeedback) return;

    this.hasSentFeedback = true;
    this.analyticsService.track(PiTrackingEventsEnum.PiFlowInitiated, { flowName: this.$scope.flowName, flowId: this.$scope.flowId, userId: getCurrentUserId() });
  };

  private generateAdditionalPiReqParams = (): Record<"gtmhubAdditionalParams", Record<string, unknown>> => {
    const hasAcceptedAnySuggestions = this.hasAcceptedTitleSuggestion || this.hasAcceptedDescriptionSuggestion;
    const shouldSendAdditionalParams = this.$scope.flowId && hasAcceptedAnySuggestions;

    return {
      ...(shouldSendAdditionalParams && { gtmhubAdditionalParams: { piFlowId: this.$scope.flowId, piFlowName: this.$scope.flowName } }),
    };
  };

  private trackPiSuggestionAccepted = (suggestionId: string): void => {
    const hasAcceptedAnySuggestions = this.hasAcceptedTitleSuggestion || this.hasAcceptedDescriptionSuggestion;
    if (!this.$scope.flowId || !hasAcceptedAnySuggestions) return;

    this.analyticsService.track(PiTrackingEventsEnum.PiSuggestionAccepted, {
      flowId: this.$scope.flowId,
      flowName: this.$scope.flowName,
      userId: getCurrentUserId(),
      suggestionId,
    });
  };

  private trackPiSuggestionEdited = (): void => {
    const hasAcceptedAnySuggestions = this.hasAcceptedTitleSuggestion || this.hasAcceptedDescriptionSuggestion;
    if (!this.$scope.flowId || !hasAcceptedAnySuggestions) return;

    const allSuggestedProps = [
      ...(this.hasAcceptedTitleSuggestion ? ["name"].concat(CreateKrSuggestionDrawerLegacyComponent.suggestedMetricPropsWithoutSuggestionCardEquivalentInUI) : []),
      ...(this.hasAcceptedDescriptionSuggestion ? ["description"] : []),
    ];
    const actualMetricValuesForSuggestedKeys = pick(this.$scope.metric, allSuggestedProps);
    const suggestedMetricValues = {
      ...(this.hasAcceptedTitleSuggestion
        ? {
            name: this.$scope.drawerTitleSection.suggestions[0].suggestionText,
            targetOperator: this.suggestedMetricFieldsNotVisibleInUI.targetOperator,
            format: this.suggestedMetricFieldsNotVisibleInUI.format,
            target: this.suggestedMetricFieldsNotVisibleInUI.target,
            initialValue: this.suggestedMetricFieldsNotVisibleInUI.initialValue,
          }
        : {}),
      ...(this.hasAcceptedDescriptionSuggestion ? { description: this.$scope.drawerDescriptionSection.suggestions[0].suggestionText } : {}),
    };
    const isMatchingOriginalMetricSuggestion = matches(actualMetricValuesForSuggestedKeys)(suggestedMetricValues);

    if (isMatchingOriginalMetricSuggestion) return;

    /** if keys & values of the $scope.metric and suggestion do not match - the suggestion has been edited */
    this.analyticsService.track(PiTrackingEventsEnum.PiSuggestionEdited, {
      flowId: this.$scope.flowId,
      flowName: this.$scope.flowName,
      userId: getCurrentUserId(),
      suggestionId: this.$scope.drawerTitleSection.suggestions[0].id,
    });
  };

  private filterBySessionId = (customFields: ICustomField[]): ICustomField[] => {
    return filterCustomFieldsBySession(this.$scope.goal.sessionId, customFields);
  };

  private isCustomFieldDisabled = (): boolean => {
    return this.$scope.goal.locked && this.$scope.isEdit;
  };

  private closeModal = ($event?: KeyboardEvent): void => {
    if (!$event || $event.key === "Enter") {
      this.$scope.$dismiss();
    }
  };
}
